import { Dimensions, Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

import { readGrandTotal, readGrandTotalOrZero } from '../../shared/slideout/rollupHelpers';

// Maps each rate metric to the underlying referral_status its rate is computed over. Used to
// recover an exact status-level count from the (entity, status) leaf groupings the slideout's
// main query already includes -- avoiding back-calculating from a server-rounded rate.
const RATE_METRIC_STATUS: Partial<Record<Metrics, string>> = {
  [Metrics.ACCEPTANCE_RATE]: 'accepted',
  [Metrics.DENIAL_RATE]: 'rejected',
  [Metrics.CANCEL_RATE]: 'canceled',
  [Metrics.PENDING_RATE]: 'pending',
};

// Sum review_id:count across leaf rows (grouping=0) where referral_status matches. The rollup
// collection strips grouping=0 rows from its groupings list, so we walk the raw responseData.
export function readStatusCount(rollups: AnalyticsResponseRollupCollection, status: string): number {
  const response = rollups.analyticsResponse;
  const statusIdx = response.findDimensionIndex(Dimensions.REFERRAL_STATUS);
  const countIdx = response.findMetricIndex(Metrics.REVIEW_ID_COUNT);
  if (statusIdx < 0 || countIdx < 0) return 0;
  return response.responseData.reduce((sum, row) => {
    if (row.grouping !== 0 || row.datesIndex !== 0) return sum;
    if (row.dimensionValues[statusIdx]?.value !== status) return sum;
    const count = Number(row.metricValues[countIdx]?.value ?? 0);
    return Number.isFinite(count) ? sum + count : sum;
  }, 0);
}

// Combined read used by every metric-detail header: total referrals + a rate + the exact count.
export function readReferralRate(
  rollups: AnalyticsResponseRollupCollection,
  rateMetric: Metrics
): { rate: number | null; count: number; total: number } {
  const total = readGrandTotalOrZero(rollups, Metrics.REVIEW_ID_COUNT);
  const rate = readGrandTotal(rollups, rateMetric);
  const status = RATE_METRIC_STATUS[rateMetric];
  const count = status ? readStatusCount(rollups, status) : 0;
  return { rate, count, total };
}
